import { useCallback } from 'react';
import { AxiosError } from 'axios';
import useToastMsg from '../common/useToastMsg';
import useUpdateProductCustomFields from '../queries/product/useUpdateProductCustomFields';
import { CustomAttributePayload } from '../../types/product/customAttribute';

interface UseCustomFieldsUpdateReturn {
  updateCustomAttributes: (formValue: CustomAttributePayload) => Promise<boolean>;
}

/**
 * 커스텀 필드 업데이트 훅
 * @param productId - 상품 ID
 * @param customAttributeKeys - 커스텀 속성 키 배열
 */
export const useCustomFieldsUpdate = (
  productId: number,
  customAttributeKeys: string[],
): UseCustomFieldsUpdateReturn => {
  const toastMsg = useToastMsg();
  const { mutateAsync: updateCustomFields } = useUpdateProductCustomFields();

  /**
   * 커스텀 필드 업데이트 함수
   * @param formValue - 폼 값
   * @returns 성공 여부
   */
  const updateCustomAttributes = useCallback(
    async (formValue: CustomAttributePayload): Promise<boolean> => {
      // customAttributeKeys가 없으면 업데이트 스킵
      if (!customAttributeKeys || customAttributeKeys.length === 0) {
        return true;
      }

      // 폼 값에서 커스텀 필드 값만 추출
      const customFieldValues = customAttributeKeys.reduce<CustomAttributePayload>((acc, key) => {
        if (key in formValue) {
          acc[key] = formValue[key];
        }
        return acc;
      }, {});

      // 추출된 커스텀 필드 값이 없으면 업데이트 스킵
      if (Object.keys(customFieldValues).length === 0) {
        return true;
      }

      try {
        await updateCustomFields({
          productId,
          payload: customFieldValues,
        });
        return true;
      } catch (error) {
        const axiosError = error as AxiosError;
        const status = axiosError?.response?.status;
        let errorMessage = '커스텀 속성 업데이트 중 오류가 발생했습니다.';

        if (status === 400) {
          errorMessage = '커스텀 속성 값이 올바르지 않습니다. 입력값을 확인해주세요.';
        } else if (status === 403) {
          errorMessage = '커스텀 속성을 수정할 권한이 없습니다.';
        } else if (status === 404) {
          errorMessage = '상품을 찾을 수 없습니다.';
        }

        toastMsg.error(errorMessage);
        return false;
      }
    },
    [productId, customAttributeKeys, updateCustomFields, toastMsg],
  );

  return { updateCustomAttributes };
};

export default useCustomFieldsUpdate;
